import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useTheme, RADIUS, TYPE } from '../theme';
import { Icon } from './ui';
import { OPEN_MARK_TAB_EVENT } from '../push';
import { huntApi, kr, HUNT_ECONOMY_CHANGED, readCollapsed, writeCollapsed, type HuntCharge, type KillReport } from '../huntEconomy';

/** Min jakt: kortet Efter jakt infällt eller utfällt, sparat i webbläsaren. */
export const AFTER_HUNT_REMINDER_COLLAPSED_KEY = 'qv_minjakt_efterjakt_collapsed';

/**
 * P4 · Kort överst i Min jakt: jakter där man inte angett fällt vilt än och obetalda avgifter.
 * Frågan kan besvaras senare – kortet ligger kvar tills man svarat (påminnelse via push/mejl också).
 */
export function AfterHuntReminder() {
  const T = useTheme();
  const [reports, setReports] = useState<KillReport[]>([]);
  const [charges, setCharges] = useState<HuntCharge[]>([]);
  const [collapsed, setCollapsed] = useState(() => readCollapsed(AFTER_HUNT_REMINDER_COLLAPSED_KEY));
  const toggle = () => setCollapsed(c => { writeCollapsed(AFTER_HUNT_REMINDER_COLLAPSED_KEY, !c); return !c; });
  const load = () => {
    huntApi.myKillReports().then(setReports).catch(() => setReports([]));
    huntApi.myHuntCharges().then(c => setCharges(c.filter(x => x.status === 'unpaid'))).catch(() => setCharges([]));
  };
  useEffect(() => {
    load();
    window.addEventListener(HUNT_ECONOMY_CHANGED, load);
    return () => window.removeEventListener(HUNT_ECONOMY_CHANGED, load);
  }, []);
  if (!reports.length && !charges.length) return null;
  const open = (markId: string) => window.dispatchEvent(new CustomEvent(OPEN_MARK_TAB_EVENT, { detail: { markId, tab: 'efterjakt' } }));
  const row = (key: string, markId: string, title: string, sub: string, urgent: boolean) => (
    <button key={key} type="button" onClick={() => open(markId)} style={{
      display: 'flex', alignItems: 'center', gap: 12, width: '100%', minHeight: 56, padding: '10px 14px', textAlign: 'left',
      background: 'transparent', border: 'none', borderTop: `1px solid ${T.border}`, color: T.text, cursor: 'pointer', fontFamily: 'inherit',
    }}>
      <span aria-hidden style={{ width: 10, height: 10, borderRadius: '50%', background: urgent ? T.danger : T.accentFill, flexShrink: 0 }} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontWeight: 700 }}>{title}</span>
        <span style={{ display: 'block', ...TYPE.small, color: T.textSub }}>{sub}</span>
      </span>
      <Icon as={ChevronRight} size={18} color={T.textMuted} />
    </button>
  );
  return (
    <div data-testid="after-hunt-reminder" style={{ maxWidth: 760, marginBottom: 18, border: `1px solid ${T.accentFill}`, borderRadius: RADIUS.lg, background: T.surface, overflow: 'hidden' }}>
      <button type="button" data-testid="after-hunt-reminder-toggle" aria-expanded={!collapsed} onClick={toggle} style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%', minHeight: 44, padding: '10px 14px', textAlign: 'left',
        background: 'transparent', border: 'none', color: T.text, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 800,
      }}>
        <span aria-hidden style={{ display: 'inline-flex', transition: 'transform .15s', transform: collapsed ? 'rotate(-90deg)' : 'none' }}><Icon as={ChevronDown} size={18} /></span>
        <span style={{ flex: 1 }}>Efter jakt</span>
        <span style={{ ...TYPE.small, fontWeight: 400, color: T.textSub }}>{reports.length + charges.length}</span>
      </button>
      {!collapsed && reports.map(r => row(`r${r.id}`, r.markId, `Vad fällde du? ${r.markName ?? ''}`.trim(),
        `${r.huntTitle ?? 'Jakt'} · ${r.huntStartedAt ? new Date(r.huntStartedAt).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' }) : ''} – ange fällt vilt eller "Inget fällt"`, false))}
      {!collapsed && charges.map(c => row(`c${c.id}`, c.markId, `Att betala ${kr(c.totalKr)} – ${c.markName ?? ''}`.trim(),
        `Fällavgifter ${c.huntDate ?? ''}${c.dueDate ? ` · förfaller ${c.dueDate}` : ''}`, c.overdue))}
    </div>
  );
}
